/**
 * ChatLabPanel Component
 * Main container for Chat Lab tab - coordinates chat testing and simulation
 */

import React from 'react';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { ChatControls } from './ChatControls';
import { SimulationPanel } from './SimulationPanel';
import { MemoryIndicator } from './MemoryIndicator';
import { AgentSelector } from './AgentSelector';
import { LiveConversationToggle } from './LiveConversationToggle';
import { useChatManagement } from '../../hooks/useChatManagement';
import type { Assistant } from '../../types';

interface ChatLabPanelProps {
  assistant: Assistant | null;
  globalPrompt?: string;
  onMemoryUpdate?: (hasMemory: boolean) => void;
  className?: string;
}

export const ChatLabPanel: React.FC<ChatLabPanelProps> = ({
  assistant,
  globalPrompt,
  onMemoryUpdate,
  className = ''
}) => {
  const {
    chatMessages,
    memoryInfo,
    conversationStats,
    activeConversationId,
    isSending,
    isSimulating,
    sendMessage,
    loadConversationHistory,
    exportConversation,
    clearConversation,
    getMemoryInfo,
    simulateConversation,
    stopSimulation
  } = useChatManagement();

  const [selectedAgentId, setSelectedAgentId] = React.useState<string | null>(null);
  const [isLiveConversation, setIsLiveConversation] = React.useState(false);
  const [showSimulation, setShowSimulation] = React.useState(false);

  // Load memory info when assistant changes
  React.useEffect(() => {
    if (assistant?.id && activeConversationId) {
      getMemoryInfo(assistant.id, activeConversationId);
    }
  }, [assistant?.id, activeConversationId, getMemoryInfo]);

  // Notify parent of memory changes
  React.useEffect(() => {
    if (onMemoryUpdate && memoryInfo) {
      onMemoryUpdate(memoryInfo.hasMemory);
    }
  }, [memoryInfo, onMemoryUpdate]);

  const handleSendMessage = async (content: string) => {
    if (!assistant?.id) return;

    await sendMessage(content, assistant.id, {
      globalPrompt,
      sessionId: activeConversationId || undefined,
      agentId: selectedAgentId || undefined
    });
  };

  const handleLoadHistory = async () => {
    if (!activeConversationId) return;
    await loadConversationHistory(activeConversationId);
  };

  const handleExport = async () => {
    if (!activeConversationId || !assistant) return;
    await exportConversation(assistant.name || 'Assistant');
  };

  const handleClear = async () => {
    if (!activeConversationId) return;
    await clearConversation(activeConversationId);
  };

  const handleSimulate = async (userMessage: string, botResponse: string, turns: number) => {
    if (!assistant?.id) return;

    await simulateConversation({
      assistantId: assistant.id,
      userMessage,
      botResponse,
      turns,
      globalPrompt,
      agentId: selectedAgentId || undefined
    });
  };

  return (
    <div className={`flex-1 flex gap-6 p-6 ${className}`}>
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <AgentSelector
              selectedAgentId={selectedAgentId || undefined}
              onSelect={(agentId: string | undefined) => setSelectedAgentId(agentId || null)}
              assistantId={assistant?.id}
            />
            <LiveConversationToggle
              enabled={isLiveConversation}
              onChange={setIsLiveConversation}
            />
          </div>

          <div className="flex items-center gap-4">
            {memoryInfo && <MemoryIndicator memoryInfo={memoryInfo} />}
            <ChatControls
              onLoadHistory={handleLoadHistory}
              onExport={handleExport}
              onClear={handleClear}
              onToggleSimulation={() => setShowSimulation(!showSimulation)}
              hasMessages={chatMessages.length > 0}
              hasConversation={!!activeConversationId}
              isSimulating={isSimulating}
              showSimulation={showSimulation}
            />
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 mb-4">
          <ChatMessageList
            messages={chatMessages}
            loading={isSending}
            conversationStats={conversationStats}
          />
        </div>

        {/* Input */}
        <ChatInput
          onSend={handleSendMessage}
          disabled={!assistant || isSending || isSimulating}
          isSending={isSending}
          placeholder={
            !assistant
              ? 'Select or create an assistant first'
              : isSimulating
              ? 'Simulation in progress...'
              : 'Type your message...'
          }
        />
      </div>

      {/* Simulation Panel (collapsible) */}
      {showSimulation && (
        <SimulationPanel
          onSimulate={handleSimulate}
          onStop={stopSimulation}
          isSimulating={isSimulating}
          disabled={!assistant}
        />
      )}
    </div>
  );
};

export default ChatLabPanel;
